import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input } from "src/@/components/ui/input";
import { Textarea } from "src/@/components/ui/textarea";

const HighlightBackdrop = ({ value, className, scrollRef }) => {
    if (!value) return null;

    const parts = value.split(/(\{\{[^}]*\}\})/g);

    return (
        <div
            ref={scrollRef}
            className={`absolute inset-0 z-0 pointer-events-none overflow-hidden ${className}`}
            style={{
                padding: '0.5rem 0.75rem',
                color: 'inherit',
                backgroundColor: 'transparent',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                textAlign: 'left',
                borderColor: 'transparent'
            }}
            aria-hidden="true"
        >
            {parts.map((part, i) => {
                if (part.match(/^\{\{.*\}\}$/)) {
                    return (
                        <span
                            key={i}
                            className="bg-emerald-100 text-emerald-700 rounded px-1 box-decoration-clone"
                            style={{ margin: '0 -1px', display: 'inline-block' }}
                        >
                            {part}
                        </span>
                    );
                }
                return <span key={i}>{part}</span>;
            })}
            {value.endsWith('\n') && <br />}
        </div>
    );
};

const VariableInput = ({ value, onChange, variables = [], multiline = false, placeholder, className = '', ...props }) => {
    const [open, setOpen] = useState(false);
    const [cursorIdx, setCursorIdx] = useState(0);
    const [rect, setRect] = useState(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const backdropRef = useRef(null);
    const menuRef = useRef(null);

    const handleScroll = useCallback(() => {
        if (inputRef.current && backdropRef.current) {
            backdropRef.current.scrollTop = inputRef.current.scrollTop;
            backdropRef.current.scrollLeft = inputRef.current.scrollLeft;
        }
    }, []);

    // Medir posição do wrapper na viewport
    const measure = useCallback(() => {
        if (!wrapperRef.current) return;
        setRect(wrapperRef.current.getBoundingClientRect());
    }, []);

    useEffect(() => {
        if (!open) return;
        measure();
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [open, measure]);

    // Fechar ao clicar fora
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapperRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const id = setTimeout(() => document.addEventListener('mousedown', handler), 10);
        return () => { clearTimeout(id); document.removeEventListener('mousedown', handler); };
    }, [open]);

    // Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') { setOpen(false); inputRef.current?.focus(); } };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open]);

    const handleChange = (e) => {
        const val = e.target.value;
        const pos = e.target.selectionStart;
        setCursorIdx(pos);
        setOpen(!!val.slice(0, pos).match(/\{\{([^}]*)$/));
        if (onChange) onChange(e);
    };

    const pick = useCallback((key) => {
        const val = value || '';
        const before = val.slice(0, cursorIdx);
        const after = val.slice(cursorIdx);
        const m = before.match(/\{\{([^}]*)$/);
        let next, cur;
        if (m) {
            const pre = before.slice(0, m.index);
            next = `${pre}{{${key}}}${after}`;
            cur = pre.length + key.length + 4;
        } else {
            next = `${val}{{${key}}}`;
            cur = next.length;
        }
        if (onChange) onChange({ target: { value: next } });
        setOpen(false);
        requestAnimationFrame(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(cur, cur);
            }
        });
    }, [value, cursorIdx, onChange]);

    const Component = multiline ? Textarea : Input;

    const grouped = variables.reduce((acc, v) => {
        const t = v.type || 'Variáveis';
        if (!acc[t]) acc[t] = [];
        acc[t].push(v);
        return acc;
    }, {});

    // Calcular se abre pra cima ou pra baixo
    const spaceBelow = rect ? window.innerHeight - rect.bottom : 999;
    const openUp = rect ? (spaceBelow < 290 && rect.top > spaceBelow) : false;

    const menuStyle = rect ? {
        position: 'fixed',
        zIndex: 99999,
        pointerEvents: 'auto',
        width: rect.width,
        left: rect.left,
        ...(openUp
            ? { bottom: window.innerHeight - rect.top + 4 }
            : { top: rect.bottom + 4 }
        ),
    } : {};

    return (
        <div className="relative w-full group" ref={wrapperRef}>
            <HighlightBackdrop value={value} className={className} scrollRef={backdropRef} />

            <Component
                ref={inputRef}
                value={value}
                onChange={handleChange}
                onScroll={handleScroll}
                placeholder={placeholder}
                className={`${className} relative z-10 !bg-transparent transition-colors`}
                style={{ color: 'transparent', caretColor: 'black' }}
                autoComplete="off"
                {...props}
            />

            {open && variables.length > 0 && rect && createPortal(
                <div
                    ref={menuRef}
                    data-variable-menu="true"
                    style={menuStyle}
                    className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden"
                    onMouseDown={(e) => {
                        // Prevenir blur do input — não afeta click nos filhos
                        e.preventDefault();
                    }}
                >
                    <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
                        {Object.entries(grouped).map(([group, vars]) => (
                            <div key={group}>
                                <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 sticky top-0 border-b border-gray-100">
                                    {group}
                                </div>
                                {vars.map((v, i) => (
                                    <div
                                        key={`${group}-${i}`}
                                        className="px-3 py-2 hover:bg-violet-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                                        onClick={() => pick(v.key)}
                                    >
                                        <div className="font-semibold text-violet-700 text-xs font-mono">
                                            {v.key}
                                        </div>
                                        {v.description && (
                                            <div className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">
                                                {v.description}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default VariableInput;
